import type { ReferenceImage } from "@/data/grass-reference-photos"
import { PhotoFigureGallery, type PhotoFigureFrame } from "@/components/ui/photo-figure-gallery"

/**
 * Species reference gallery for a grass guide's "How to Identify" section: the
 * maintained-turf recognition lead first, then the curated alternates (blade or
 * collar macro, seed head, stolon).
 *
 * Replaces the single-photo GrassIdentificationPhoto. The guide's Article
 * ImageObject nodes (app/guides/[slug]/page.tsx) and sitemap-image.xml both
 * list `lead + alternates`, which is exactly this list, so every image they
 * declare is one the reader can see and every CC BY frame carries its credit.
 *
 * Presentation is shared with the weed identification galleries via
 * PhotoFigureGallery; this is the adapter from ReferenceImage.
 */
export function GrassReferenceGallery({
  images,
  grassName,
}: {
  images: ReferenceImage[]
  grassName: string
}) {
  // getGrassReferenceImages already returns the lead followed by its
  // alternates, but dedupe by src so a record that repeats its lead among the
  // alternates cannot render the same photo twice or emit a duplicate React key.
  const seen = new Set<string>()
  const frames: PhotoFigureFrame[] = []
  for (const image of images) {
    if (seen.has(image.src)) continue
    seen.add(image.src)
    frames.push({
      src: image.src,
      alt: image.caption
        ? `${grassName} grass, ${image.caption.replace(/\s*·\s*/g, ", ")}`
        : `What ${grassName} grass looks like in a lawn`,
      caption: image.caption,
      credit: image.credit,
      license: image.license,
      sourceUrl: image.sourceUrl,
    })
  }

  return <PhotoFigureGallery frames={frames} heading={`What ${grassName} looks like`} />
}
